// FLAGS: silent-failure
// Tag reads and writes — session type, language, topics, expert status — plus the per-client availability variants.
import {
  useMutation,
  useQuery,
  useQueryClient,
  useQueries,
  UseQueryResult,
  UseQueryOptions,
} from "@tanstack/react-query";
import { apiPaths } from "@src/constants";
import { queryIdentifiers } from "@src/utils/constants";
import { Tags } from "@src/api/v1/Tags";
import { PsychologistManagement } from "@src/api/v1/PsychologistManagement";
import { Clients } from "@src/api/v1/Clients";
import { Psychologists } from "@src/api/v1/Psychologists";
import { OpenUpHttpClient } from "@src/api/OpenUpHttpClient";
import { ModelsTagType } from "@src/api/v1/data-contracts";

export const sessionTypeTagName = "SessionType";
export const sessionLanguageTagName = "SessionLanguage";
export const psychologistStatusTagName = "PsychologistStatus";
export const psychologistLanguageTagName = "PsychologistLanguage";
export const topicsTagName = "Topics";
export const psychologistGenderTagName = "Gender";

const consultsIdentifier = "consults";
const fetchTags = async (tagType: string): Promise<ModelsTagType> => {
  const tags = new Tags(OpenUpHttpClient);
  return tags.getTags(tagType);
};

const fetchTagsAvailableForClient = async (
  tagType: string,
  availableForUserId: string | undefined,
): Promise<ModelsTagType> =>
  new Tags(OpenUpHttpClient).getTagsAvailableForClient({
    tagType,
    availableForUserId,
  });

// FLAGS: silent-failure
// `result.data!` asserts away the pending and failed cases, so `taggedData[tagType]` is `undefined` for any tag type
// whose query has not resolved — a caller reading `.tags` on it throws instead of seeing the reported `error`.
const processMultipleTagsQueryResult = (
  queyTagTypes: string[],
  results: UseQueryResult<ModelsTagType, Error>[],
) => {
  const data = results.map((result) => result.data!);

  const isLoading = results.some((result) => result.isLoading);
  const error = results.find((result) => result.error)?.error || null;

  const taggedData = queyTagTypes.reduce(
    (acc: { [key: string]: ModelsTagType }, tagType, index) => {
      acc[tagType] = data[index];
      return acc;
    },
    {},
  );

  return { taggedData, isLoading, error };
};

const useCreateMultipleTagsQueries = (
  tagTypes: string[],
  queryFnGenerator: (tagType: string) => {
    queryKey: unknown[];
    queryFn: () => Promise<ModelsTagType>;
  },
) => {
  const queries = tagTypes.map((tagType) => queryFnGenerator(tagType));
  const results = useQueries({ queries });
  return processMultipleTagsQueryResult(tagTypes, results);
};

export const useMultipleTagsQueries = (tagTypes: string[]) => {
  return useCreateMultipleTagsQueries(tagTypes, (tagType) => ({
    queryKey: [queryIdentifiers.tags, tagType],
    queryFn: () => fetchTags(tagType),
  }));
};

export const useMultipleTagsAvailableForClientQueries = (
  tagTypes: string[],
  availableForUserId: string | undefined = undefined,
) => {
  return useCreateMultipleTagsQueries(tagTypes, (tagType) => ({
    queryKey: [queryIdentifiers.tags, tagType, availableForUserId],
    queryFn: () => fetchTagsAvailableForClient(tagType, availableForUserId),
  }));
};

export const useUserTagsQuery = (userId: string, tagType: string) => {
  const queryClient = useQueryClient();

  const { data: userTags } = useQuery({
    queryKey: [apiPaths.psychologistManagement, userId, tagType],
    queryFn: () =>
      new PsychologistManagement(OpenUpHttpClient).getUserTags(userId, tagType),
  });

  const { mutateAsync: saveTags } = useMutation({
    mutationFn: (tags: string[]) =>
      new PsychologistManagement(OpenUpHttpClient).setUserTags(
        userId,
        tagType,
        tags,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [apiPaths.psychologistManagement, userId, tagType],
      });
    },
  });

  return {
    userTags,
    saveTags,
  };
};

export const useConsultTagsQuery = (
  clientId: string | undefined,
  consultId: number | undefined,
  tagType: string,
) => {
  const queryClient = useQueryClient();

  const { data: consultTags } = useQuery({
    queryKey: [
      apiPaths.clients,
      clientId,
      consultsIdentifier,
      consultId,
      tagType,
    ],
    queryFn: () =>
      new Clients(OpenUpHttpClient).getConsultTags2(
        consultId!,
        tagType,
        clientId!,
      ),
    enabled: !!(clientId && consultId),
  });

  const { mutateAsync: saveTags } = useMutation({
    mutationFn: ({ tags, id }: { tags: string[]; id: number }) =>
      new Clients(OpenUpHttpClient).setConsultTags(
        consultId ?? id,
        tagType,
        clientId!,
        tags,
      ),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          apiPaths.clients,
          clientId,
          consultsIdentifier,
          consultId,
          tagType,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [apiPaths.clients, clientId, consultsIdentifier],
      });
    },
  });

  return {
    consultTags,
    saveTags,
  };
};

export const usePsychologistTagsQuery = (clientId: string) =>
  useQuery({
    queryKey: [apiPaths.psychologistsTags, clientId],
    queryFn: () =>
      new Psychologists(OpenUpHttpClient).getLoggedInExpertTags({ clientId }),
  });

export const usePsychologistTagsByIdQuery = (
  psychologistId: string | undefined,
  clientId: string,
) =>
  useQuery({
    queryKey: [apiPaths.psychologistsTags, psychologistId, clientId],
    queryFn: () =>
      new Psychologists(OpenUpHttpClient).getExpertTags({
        expertId: psychologistId!,
        clientId,
      }),
    enabled: !!psychologistId,
  });

export const useSessionNoteTopicsByTheme = (
  themeKey: string | undefined,
  options?: Omit<UseQueryOptions<string[]>, "queryKey" | "queryFn" | "enabled">,
) =>
  useQuery({
    queryKey: [apiPaths.sessionNoteTopicsByTheme, themeKey],
    queryFn: () =>
      new Tags(OpenUpHttpClient).getSessionNoteTopicsByTheme(themeKey!),
    enabled: !!themeKey,
    ...options,
  });
